import { useMemo, useState } from "react";
import { GALLERY, FILTERS } from "@/lib/data";
import { X, PlayCircle } from "lucide-react";
import { Link } from "react-router-dom";
import SectionTitle from "@/components/SectionTitle";
import RotatingVideo from "@/components/RotatingVideo";
import { GALLERY_VIDEOS } from "@/lib/videos";

export default function Gallery() {
    const [filter, setFilter] = useState("All");
    const [preview, setPreview] = useState(null);

    const items = useMemo(() => filter === "All" ? GALLERY : GALLERY.filter((g) => g.tag === filter), [filter]);

    return (
        <div data-testid="gallery-page">
            <RotatingVideo
                videos={GALLERY_VIDEOS}
                eager
                testId="gallery-page-video"
                className="relative w-full h-[92vh] min-h-[640px]"
                overlay={
                    <>
                        <div className="absolute inset-0 bg-gradient-to-t from-navy/75 via-navy/10 to-navy/40" />
                        <div className="absolute inset-0 max-w-7xl mx-auto px-6 lg:px-10 flex flex-col justify-end pb-16 md:pb-24">
                            <div className="text-[11px] uppercase tracking-[0.28em] text-gold mb-4 flex items-center gap-3"><span className="w-10 h-[1px] bg-gold" /> Gallery</div>
                            <h1 className="font-display text-5xl md:text-7xl lg:text-8xl text-ivory max-w-3xl leading-[1.02]">A moving picture of <span className="font-serif-italic text-gold">Serenity Cove</span>.</h1>
                            <p className="mt-6 text-ivory/85 max-w-xl leading-relaxed">Cinematic frames from the cove, the suites, the spa and the island beyond.</p>
                            <div className="mt-8 flex items-center gap-3">
                                <a href="#gallery-grid" data-testid="gallery-view-btn" className="btn-gold px-6 py-3 rounded-full inline-flex items-center gap-2"><PlayCircle size={14} /> View Gallery</a>
                                <a href="#gallery-grid" data-testid="gallery-watch-more-btn" className="btn-outline-ivory px-6 py-3 rounded-full">Watch More</a>
                            </div>
                        </div>
                    </>
                }
            />
            <div id="gallery-grid" className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-8">
                <div className="flex flex-wrap gap-2">
                    {FILTERS.map((f) => (
                        <button
                            key={f}
                            data-testid={`gallery-filter-${f.toLowerCase().replace(/\s+/g, "-")}`}
                            onClick={() => setFilter(f)}
                            className={`text-[11px] uppercase tracking-[0.2em] px-4 py-2 rounded-full border transition-colors ${filter === f ? "bg-navy text-ivory border-navy" : "border-navy/20 text-navy hover:border-gold hover:text-gold"}`}
                        >
                            {f}
                        </button>
                    ))}
                </div>
            </div>
            <div className="max-w-7xl mx-auto px-6 lg:px-10 pb-32">
                <div className="columns-1 sm:columns-2 lg:columns-3 gap-4 [&>*]:mb-4">
                    {items.map((g, i) => (
                        <button key={i} data-testid={`gallery-item-${i}`} onClick={() => setPreview(g)} className="block w-full break-inside-avoid overflow-hidden rounded-xl group">
                            <div className="relative">
                                <img src={g.url} alt={g.cap} loading="lazy" className="w-full object-cover group-hover:scale-105 transition-transform duration-1000" />
                                <div className="absolute inset-0 bg-gradient-to-t from-navy/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                                    <div className="absolute bottom-4 left-4 right-4 text-ivory">
                                        <div className="text-[10px] uppercase tracking-[0.24em] text-gold">{g.tag}</div>
                                        <div className="font-serif-italic text-lg mt-1">{g.cap}</div>
                                    </div>
                                </div>
                            </div>
                        </button>
                    ))}
                </div>
            </div>

            {preview && (
                <div data-testid="gallery-lightbox" className="fixed inset-0 z-50 bg-navy/85 backdrop-blur-md flex items-center justify-center p-6" onClick={() => setPreview(null)}>
                    <button onClick={() => setPreview(null)} className="absolute top-6 right-6 text-ivory hover:text-gold"><X size={22} /></button>
                    <div className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
                        <img src={preview.url} alt={preview.cap} className="w-full max-h-[80vh] object-contain rounded-xl" />
                        <div className="mt-4 text-ivory/80 text-center font-serif-italic">{preview.cap}</div>
                    </div>
                </div>
            )}
        </div>
    );
}
